import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { clearFamilyLogo, uploadFamilyLogo } from "../lib/uploadLogo";
import type { LogoVariant, Membership } from "@family/shared";

interface Props {
  familySlug: string;
  membership: Membership;
}

/**
 * Admin UI for uploading per-family logos. Two slots — light (used over
 * dark backgrounds) and dark (used over light backgrounds, e.g. the app
 * header). Each slot supports upload and clear.
 */
export function LogoBranding({ familySlug, membership }: Props) {
  return (
    <div className="stack">
      <LogoSlot
        familySlug={familySlug}
        variant="light"
        label="Light variant"
        helper="Designed for LIGHT backgrounds (the app header). Use a logo with dark/black strokes."
        previewBg="#f3f4f6"
        currentUrl={membership.logoLightUrl}
      />
      <LogoSlot
        familySlug={familySlug}
        variant="dark"
        label="Dark variant"
        helper="Designed for DARK backgrounds. Use a logo with light/white strokes."
        previewBg="#1f2937"
        currentUrl={membership.logoDarkUrl}
      />
      <p className="text-subtle" style={{ fontSize: "0.85rem" }}>
        PNG, SVG, WebP or JPEG. Max 2 MB. Square or wide formats both work; the
        header sizes by height. If a slot is empty the bundled default is used.
      </p>
    </div>
  );
}

function LogoSlot({
  familySlug,
  variant,
  label,
  helper,
  previewBg,
  currentUrl,
}: {
  familySlug: string;
  variant: LogoVariant;
  label: string;
  helper: string;
  previewBg: string;
  currentUrl: string | null;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadFamilyLogo(familySlug, variant, file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  const clear = useMutation({
    mutationFn: () => clearFamilyLogo(familySlug, variant),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="card stack">
      <div className="row-between">
        <div>
          <strong>{label}</strong>
          <div className="text-subtle" style={{ fontSize: "0.85rem", marginTop: 4 }}>
            {helper}
          </div>
        </div>
      </div>

      <div
        style={{
          background: previewBg,
          borderRadius: 6,
          padding: 16,
          minHeight: 88,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {currentUrl ? (
          <img
            src={currentUrl}
            alt={`${label} preview`}
            style={{ maxHeight: 56, maxWidth: "100%", objectFit: "contain" }}
          />
        ) : (
          <span
            style={{
              color: previewBg === "#1f2937" ? "#9ca3af" : "#6b7280",
              fontSize: "0.85rem",
            }}
          >
            (using bundled default)
          </span>
        )}
      </div>

      <div style={{ display: "flex", gap: "var(--s-2)", flexWrap: "wrap" }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/svg+xml,image/webp,image/jpeg"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) upload.mutate(file);
            // Reset so re-selecting the same file fires onChange again.
            e.target.value = "";
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending}
          style={{ minHeight: 34, padding: "4px 14px", fontSize: "0.85rem" }}
        >
          {upload.isPending
            ? "Uploading…"
            : currentUrl
              ? "Replace"
              : "Upload"}
        </button>
        {currentUrl && (
          <button
            type="button"
            className="btn-tertiary"
            onClick={() => {
              if (window.confirm("Remove this logo and revert to the default?")) {
                clear.mutate();
              }
            }}
            disabled={clear.isPending}
            style={{
              minHeight: 34,
              padding: "4px 14px",
              fontSize: "0.85rem",
              color: "var(--c-danger)",
            }}
          >
            Remove
          </button>
        )}
      </div>

      {error && <p className="text-error" style={{ fontSize: "0.85rem" }}>{error}</p>}
    </div>
  );
}
